import { useState, useEffect } from 'react';
import { useApi } from '../../api';
import {
  fmtTime, fmtDuration,
  consolidatedLabel, consolidatedTone,
  consolidateSummary, CONSOLIDATED_BUCKETS,
} from './statusUtils';
import { PresenceTag, DayTypeTag } from './AttendanceTags';
import StatusTag from '../../components/common/StatusTag';

/** UC1 (daily log + sessions) and UC4 (monthly consolidated) for one employee. */
export interface EmployeeDetailProps {
  employeeId: string;
  onClose: () => void;
}

export default function EmployeeDetail({ employeeId, onClose }: EmployeeDetailProps) {
  const { attendance } = useApi();
  const [emp, setEmp] = useState<any>(null);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [day, setDay] = useState<any>(null);
  const [sessions, setSessions] = useState<any[]>([]);
  const [monthly, setMonthly] = useState<any>(null);
  const [sightings, setSightings] = useState<any[]>([]);

  useEffect(() => { (async () => { try { setEmp(await attendance.getEmployee(employeeId)); } catch (e) { console.error(e); } })(); }, [attendance, employeeId]);
  useEffect(() => { (async () => {
    try {
      const d = await attendance.employeeDay(employeeId, date);
      setDay(d?.day || null); setSessions(d?.sessions || []);
      const s = await attendance.employeeSightings(employeeId, { from: `${date}T00:00:00`, to: `${date}T23:59:59`, limit: 200 });
      setSightings(s?.data || []);
    } catch (e) { console.error(e); }
  })(); }, [attendance, employeeId, date]);
  useEffect(() => { (async () => { try { setMonthly(await attendance.employeeMonthly(employeeId, month)); } catch (e) { console.error(e); } })(); }, [attendance, employeeId, month]);

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-end z-50" onClick={onClose}>
      <div className="bg-card w-full max-w-3xl h-full overflow-y-auto shadow-modal border-l border-border p-4 sm:p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-5">
          <div>
            <h2 className="text-xl font-semibold text-foreground">{emp?.name || 'Employee'}</h2>
            {/* Same order as the board that opened this: code, then what they do, then where.
                A drawer that re-orders the person's own sub-line reads as a different person. */}
            <div className="text-sm text-muted-foreground">
              {[emp?.employee_code, emp?.designation, emp?.department].filter(Boolean).join(' · ')}
            </div>
          </div>
          <button onClick={onClose} className="px-2 py-1 rounded text-sm border border-border hover:bg-muted">Close</button>
        </div>

        {/* ── Daily ── */}
        <section className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="font-medium text-foreground">Daily log</h3>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="ml-auto px-2 py-1 text-sm rounded border border-border bg-background text-foreground" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <Stat label="First in" value={fmtTime(day?.first_in_at)} />
            <Stat label="Last out" value={fmtTime(day?.last_out_at)} />
            <Stat label="Total inside" value={fmtDuration(day?.total_inside_minutes)} />
            {/* Daily log → Present / Absent; the day-type split lives in Monthly below. */}
            <Stat label="Status" value={<PresenceTag status={day?.status} />} />
          </div>
          <div className="rounded-lg border border-border overflow-hidden overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-card text-muted-foreground text-xs"><tr className="text-left"><th className="px-3 py-2">In</th><th className="px-3 py-2">Out</th><th className="px-3 py-2">Duration</th></tr></thead>
              <tbody>
                {sessions.length === 0 && <tr><td colSpan={3} className="px-3 py-4 text-center text-muted-foreground">No sessions.</td></tr>}
                {sessions.map((s) => (
                  <tr key={s.id} className="border-t border-border">
                    <td className="px-3 py-2">{fmtTime(s.in_at)}</td>
                    <td className="px-3 py-2">
                      {s.is_open
                        ? <span className="text-success">Still in</span>
                        : s.out_source === 'event'
                          ? fmtTime(s.out_at)
                          : <span className="text-muted-foreground" title="No exit was recorded by a camera — session auto-closed at the last time they were seen.">≈ {fmtTime(s.out_at)} (no out record)</span>}
                    </td>
                    <td className="px-3 py-2">{fmtDuration(s.duration_minutes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Raw in/out log: every camera sighting with its resolved direction ── */}
          {sightings.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-foreground mb-2">In/Out log <span className="text-xs text-muted-foreground font-normal">({sightings.length} sightings on {date})</span></h4>
              <div className="rounded-lg border border-border max-h-64 overflow-y-auto overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-card text-muted-foreground text-xs sticky top-0"><tr className="text-left"><th className="px-3 py-2">Time</th><th className="px-3 py-2">In/Out</th><th className="px-3 py-2">Camera</th></tr></thead>
                  <tbody>
                    {sightings.map((sg) => (
                      <tr key={sg.id} className="border-t border-border">
                        <td className="px-3 py-2">{fmtTime(sg.seen_at)}</td>
                        <td className="px-3 py-2">
                          {sg.inout === 'in' && <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase bg-success/15 text-success">↓ In</span>}
                          {sg.inout === 'out' && <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase bg-destructive/15 text-destructive">↑ Out</span>}
                          {sg.inout !== 'in' && sg.inout !== 'out' && <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase bg-muted text-muted-foreground" title="Camera couldn't determine walking direction">Seen</span>}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">{sg.camera_id || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>

        {/* ── Monthly ── */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <h3 className="font-medium text-foreground">Monthly</h3>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="ml-auto px-2 py-1 text-sm rounded border border-border bg-background text-foreground" />
          </div>
          {/* Consolidated day types — Full day / Half day / Absent, in a fixed order so
              the chips don't reshuffle between months. `partial` days fold into Absent
              because they earn no day credit. */}
          {monthly?.summary && (() => {
            const buckets = consolidateSummary(monthly.summary);
            return (
              <div className="flex gap-2 mb-3 flex-wrap">
                {CONSOLIDATED_BUCKETS.map((b) => (
                  <StatusTag key={b} tone={consolidatedTone(b)}>
                    {consolidatedLabel(b)}
                    <span className="ml-0.5 tabular-nums">{buckets[b]}</span>
                  </StatusTag>
                ))}
              </div>
            );
          })()}
          <div className="rounded-lg border border-border overflow-hidden overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-card text-muted-foreground text-xs"><tr className="text-left"><th className="px-3 py-2 whitespace-nowrap">Date</th><th className="px-3 py-2 whitespace-nowrap">First in</th><th className="px-3 py-2 whitespace-nowrap">Last out</th><th className="px-3 py-2">Total</th><th className="px-3 py-2">Status</th></tr></thead>
              <tbody>
                {(!monthly?.days || monthly.days.length === 0) && <tr><td colSpan={5} className="px-3 py-4 text-center text-muted-foreground">No data for this month.</td></tr>}
                {monthly?.days?.map((d: any) => (
                  <tr key={d.work_date} className="border-t border-border">
                    <td className="px-3 py-2">{d.work_date}</td>
                    <td className="px-3 py-2">{fmtTime(d.first_in_at)}</td>
                    <td className="px-3 py-2">{fmtTime(d.last_out_at)}</td>
                    <td className="px-3 py-2">{fmtDuration(d.total_inside_minutes)}</td>
                    <td className="px-3 py-2"><DayTypeTag status={d.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}

/** `value` may be text or a ready-made element (e.g. a StatusTag). */
function Stat({ label, value }: { label: string; value: any }) {
  const isEl = typeof value === 'object' && value !== null;
  return (
    <div className="bg-background border border-border rounded-lg p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`mt-1 ${isEl ? '' : 'text-sm font-medium text-foreground'}`}>{value}</div>
    </div>
  );
}
